import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { Modal, Button } from 'react-bootstrap'
import BackButton from '../ChildComponent/BackButton'
import COLORS from '../resources/colors/COLORS'
import SessionManager from '../Utils/SessionManager'
import myToast from '../Utils/MyToast'
import MyLoader from '../Spinner/MyLoader'
import '../css/Admin_css/admin-customize.css'
import MdlManageDigitalSign from '../dialog/MdlManageDigitalSign'

const SignSettings = () => {
  const [loading, setLoading] = useState(false)
  const [digitalSigns, setDigitalSigns] = useState([])
  const [imageErrors, setImageErrors] = useState({})
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedSign, setSelectedSign] = useState(null)
  const [selectedSignId, setSelectedSignId] = useState(null)
  const [sampleSignSettings, setSampleSignSettings] = useState([])
  const [sampleCache, setSampleCache] = useState({})
  const [loadingSamples, setLoadingSamples] = useState(false)
  const [updatingSampleId, setUpdatingSampleId] = useState(null)

  useEffect(() => {
    window.scrollTo(0, 0)
    fetchSchoolDigitalSign()
  }, [])

  const fetchSchoolDigitalSign = async () => {
    try {
      setLoading(true)
      const schoolCode = SessionManager.getSchoolCode && SessionManager.getSchoolCode()
      
      if (!schoolCode) {
        myToast.notify_warning('School code not found')
        setDigitalSigns([])
        setLoading(false)
        return
      }

      const FUNCTION_NAME = 'getSchoolDigitalSign.php'
      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`
      
      const response = await axios.get(URL, {
        params: {
          school_code: schoolCode
        }
      })

      if (response.data?.api_status === 'OK') {
        const signsData = response.data.data || []
        setDigitalSigns(signsData)
        if (signsData.length > 0) {
          console.log('Digital signs loaded:', signsData.length, signsData)
          myToast.notify_success(`Loaded ${signsData.length} digital sign(s)`)
        } else {
          console.log('No digital signs found')
          myToast.notify_info('No digital signs found for this school')
        }
      } else {
        console.error('API Error:', response.data)
        myToast.notify_warning(response.data?.api_remark || 'Failed to load digital signs')
        setDigitalSigns([])
      }
    } catch (error) {
      console.error('Error fetching school digital sign:', error)
      myToast.notify_error('Failed to load digital signs')
      setDigitalSigns([])
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (sign) => {
    setSelectedSign(sign)
    setShowEditModal(true)
  }

  const handleDelete = (sign) => {
    setSelectedSign(sign)
    setShowDeleteModal(true)
  }

  const handleAddNew = () => {
    setSelectedSign(null)
    setSelectedSignId(null)
    setShowEditModal(false)
    setShowAddModal(true)
    myToast.notify_info('Opening Add Sign dialog')
  }

  const confirmDelete = async () => {
    // TODO: Implement delete API call
    myToast.notify_info('Delete functionality will be implemented')
    setShowDeleteModal(false)
    setSelectedSign(null)
  }

  const handleSignSelect = async (sign) => {
    try {
      if (!sign?.id) {
        myToast.notify_warning('Sign id not found for this record')
        return
      }

      // Avoid unnecessary fetch if we already have data cached
      if (sampleCache[sign.id]) {
        setSelectedSignId(sign.id)
        setSelectedSign(sign)
        setSampleSignSettings(sampleCache[sign.id])
        return
      }

      setLoadingSamples(true)
      setSelectedSignId(sign.id)
      setSelectedSign(sign)
      
      const schoolCode = SessionManager.getSchoolCode && SessionManager.getSchoolCode()
      
      if (!schoolCode) {
        myToast.notify_warning('School code not found')
        return
      }

      const FUNCTION_NAME = 'getSampleSignSettings.php'
      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`
      
      const response = await axios.get(URL, {
        params: {
          school_code: schoolCode,
          authorized_sign_id: sign.authorized_sign_id || sign.id
        }
      })

      if (response.data?.api_status === 'OK') {
        const samplesData = response.data.data || []
        setSampleSignSettings(samplesData)
        setSampleCache((prev) => ({ ...prev, [sign.id]: samplesData }))
        myToast.notify_success(`Loaded ${samplesData.length} sample settings`)
      } else {
        myToast.notify_warning(response.data?.api_remark || 'Failed to load sample settings')
        setSampleSignSettings([])
      }
    } catch (error) {
      console.error('Error fetching sample sign settings:', error)
      myToast.notify_error('Failed to load sample settings')
      setSampleSignSettings([])
    } finally {
      setLoadingSamples(false)
    }
  }

  const toggleSampleStatus = async (sample) => {
    try {
      if (!sample.sign_id) {
        myToast.notify_warning('Sign record not found for this sample')
        return
      }

      const schoolCode = SessionManager.getSchoolCode && SessionManager.getSchoolCode()
      if (!schoolCode) {
        myToast.notify_warning('School code not found')
        return
      }

      const newStatus = sample.sign_status === 'Active' ? 'InActive' : 'Active'
      setUpdatingSampleId(sample.sign_id)

      const FUNCTION_NAME = 'UpdateSampleSignStatus.php'
      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`

      const response = await axios.get(URL, {
        params: {
          school_code: schoolCode,
          sign_id: sample.sign_id,
          status: newStatus,
        },
      })

      if (response.data?.api_status === 'OK') {
        setSampleSignSettings((prev) =>
          prev.map((s) =>
            s.sign_id === sample.sign_id
              ? { ...s, sign_status: newStatus, is_newly_added: false }
              : s
          )
        )
        setSampleCache((prev) => ({
          ...prev,
          [selectedSignId]: (prev[selectedSignId] || []).map((s) =>
            s.sign_id === sample.sign_id ? { ...s, sign_status: newStatus, is_newly_added: false } : s
          ),
        }))
        myToast.notify_success(`Sign set to ${newStatus}`)
      } else {
        myToast.notify_warning(response.data?.api_remark || 'Failed to update sign status')
      }
    } catch (error) {
      console.error('Error updating sign status:', error)
      myToast.notify_error('Error updating sign status')
    } finally {
      setUpdatingSampleId(null)
    }
  }

  if (loading) {
    return <MyLoader />
  }

  return (
    <div className="content">
      <div className="content-header d-flex justify-content-between align-items-center">
        <div className="leftside-content-header d-flex align-items-center">
          <i
            className="fa fa-home"
            aria-hidden="true"
            style={{ fontSize: '18px', color: COLORS.primary_color, cursor: 'pointer', marginRight: '10px' }}
            onClick={() => (window.location.href = '/AdminMain')}
          />
          <ul className="breadcrumbs mb-0">
            <li>Settings</li>
            <li>Sign Settings</li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="row bg_page_white_rounded" style={{ '--mMargin': '50px' }}>
        <div className="col-sm-12">
          <div className="row box_shadow bg_card_blue_outline" style={{ padding: 2, margin: '10px 0px 10px 0px' }}>
            <div className="col-sm-12" style={{ height: '100%', margin: 0, padding: '5px', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h6 style={{ margin: '2px 0 4px 0', fontSize: '16px', fontWeight: '700', color: 'white', backgroundColor: COLORS.primary_color || '#3b82f6', padding: '8px 12px', borderRadius: '6px', display: 'inline-block' }}>
                  <i className="fa fa-university" style={{ marginRight: '8px' }} />
                  Sign Settings
                </h6>
                <button
                  type="button"
                  onClick={handleAddNew}
                  style={{
                    backgroundColor: COLORS.primary_color || '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.primary_color ? '#2563eb' : '#2563eb'
                    e.target.style.transform = 'translateY(-1px)'
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = COLORS.primary_color || '#3b82f6'
                    e.target.style.transform = 'translateY(0)'
                  }}
                >
                  <i className="fa fa-plus" style={{ fontSize: '12px' }}></i>
                  Add New Sign
                </button>
              </div>
              <div style={{ padding: '16px', color: '#374151' }}>
                {digitalSigns.length > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                    {digitalSigns.map((sign, index) => (
                      <div
                        key={sign.id || index}
                        onClick={() => handleSignSelect(sign)}
                        style={{
                          background: selectedSignId === sign.id 
                            ? 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)' 
                            : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                          borderRadius: '12px',
                          border: selectedSignId === sign.id 
                            ? '2px solid #3b82f6' 
                            : '2px solid #e5e7eb',
                          padding: '16px',
                          boxShadow: selectedSignId === sign.id 
                            ? '0 4px 12px rgba(59, 130, 246, 0.3)' 
                            : '0 2px 8px rgba(0, 0, 0, 0.08)',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          position: 'relative',
                        }}
                        onMouseEnter={(e) => {
                          if (selectedSignId !== sign.id) {
                            e.currentTarget.style.transform = 'translateY(-2px)'
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)'
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedSignId !== sign.id) {
                            e.currentTarget.style.transform = 'translateY(0)'
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)'
                          }
                        }}
                      >
                        {selectedSignId === sign.id && (
                          <div style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: 'bold',
                          }}>
                            <i className="fa fa-check"></i>
                          </div>
                        )}
                        {/* Sign Image */}
                        {sign.scanned_sign && (
                          <div
                            style={{
                              width: '100%',
                              height: '120px',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              marginBottom: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: '#f9fafb',
                              overflow: 'hidden',
                            }}
                          >
                            {imageErrors[sign.id] ? (
                              <div style={{ color: '#9ca3af', fontSize: '12px' }}>Image not available</div>
                            ) : (
                              <img
                                src={`${process.env.REACT_APP_SERVER_NAME}${sign.scanned_sign}`}
                                alt={sign.authority_name || 'Digital Sign'}
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                  objectFit: 'contain',
                                }}
                                onError={() => {
                                  setImageErrors((prev) => ({ ...prev, [sign.id]: true }))
                                }}
                              />
                            )}
                          </div>
                        )}
                        
                        {/* Authority Details */}
                        <div style={{ marginBottom: '8px' }}>
                          <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827', marginBottom: '4px' }}>
                            {sign.authority_name || 'N/A'}
                          </div>
                          {sign.authority_designation && (
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                              {sign.authority_designation}
                            </div>
                          )}
                        </div>
                        
                        {/* Additional Info */}
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
                          {sign.status && (
                            <div style={{ marginBottom: '4px' }}>
                              <span style={{ fontWeight: '600' }}>Status:</span>{' '}
                              <span
                                style={{
                                  color: sign.status === 'Active' ? '#10b981' : '#ef4444',
                                  fontWeight: '600',
                                }}
                              >
                                {sign.status}
                              </span>
                            </div>
                          )}
                          {sign.date && (
                            <div style={{ marginBottom: '4px' }}>
                              <span style={{ fontWeight: '600' }}>Date:</span> {new Date(sign.date).toLocaleDateString()}
                            </div>
                          )}
                          {sign.remark && (
                            <div style={{ marginTop: '4px', fontStyle: 'italic' }}>
                              {sign.remark}
                            </div>
                          )}
                        </div>
                        
                        {/* Action Buttons */}
                        <div style={{ marginTop: '12px', display: 'flex', gap: '8px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }} onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              handleEdit(sign)
                            }}
                            style={{
                              flex: 1,
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '6px 12px',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px',
                              transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#2563eb'
                              e.target.style.transform = 'translateY(-1px)'
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#3b82f6'
                              e.target.style.transform = 'translateY(0)'
                            }}
                          >
                            <i className="fa fa-edit" style={{ fontSize: '11px' }}></i>
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              handleDelete(sign)
                            }}
                            style={{
                              flex: 1,
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '6px 12px',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px',
                              transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#dc2626'
                              e.target.style.transform = 'translateY(-1px)'
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#ef4444'
                              e.target.style.transform = 'translateY(0)'
                            }}
                          >
                            <i className="fa fa-trash" style={{ fontSize: '11px' }}></i>
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                    <i className="fa fa-signature" style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}></i>
                    <p style={{ fontSize: '14px', fontWeight: '600' }}>No digital signs found</p>
                  </div>
                )}
              </div>
              
              {/* Sample Sign Settings Section */}
              {selectedSignId && sampleSignSettings.length > 0 && (
                <div style={{ marginTop: '24px', padding: '16px', borderTop: '2px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h6 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <i className="fa fa-list" style={{ color: COLORS.primary_color || '#3b82f6' }}></i>
                      Sample Settings for: {selectedSign?.authority_name || 'Selected Sign'}
                    </h6>
                  </div>
                  <div
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      boxShadow: '0 12px 30px rgba(15, 23, 42, 0.08)',
                    }}
                  >
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'separate',
                        borderSpacing: 0,
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            background: 'linear-gradient(90deg, #eff6ff 0%, #e0f2fe 50%, #f9fafb 100%)',
                            borderBottom: '2px solid #e5e7eb',
                          }}
                        >
                          <th
                            style={{
                              padding: '12px',
                              textAlign: 'center',
                              fontSize: '13px',
                              fontWeight: '700',
                              color: '#1f2937',
                              borderRight: '1px solid #e5e7eb',
                              width: '70px',
                            }}
                          >
                            Sr No
                          </th>
                          <th
                            style={{
                              padding: '12px',
                              textAlign: 'left',
                              fontSize: '13px',
                              fontWeight: '700',
                              color: '#1f2937',
                              borderRight: '1px solid #e5e7eb',
                            }}
                          >
                            Sample ID
                          </th>
                          <th
                            style={{
                              padding: '12px',
                              textAlign: 'left',
                              fontSize: '13px',
                              fontWeight: '700',
                              color: '#1f2937',
                              borderRight: '1px solid #e5e7eb',
                            }}
                          >
                            Sample Name
                          </th>
                          <th
                            style={{
                              padding: '12px',
                              textAlign: 'left',
                              fontSize: '13px',
                              fontWeight: '700',
                              color: '#1f2937',
                              borderRight: '1px solid #e5e7eb',
                            }}
                          >
                            Document Caption
                          </th>
                          <th
                            style={{
                              padding: '12px',
                              textAlign: 'left',
                              fontSize: '13px',
                              fontWeight: '700',
                              color: '#1f2937',
                              borderRight: '1px solid #e5e7eb',
                            }}
                          >
                            Remark
                          </th>
                          <th
                            style={{
                              padding: '12px',
                              textAlign: 'center',
                              fontSize: '13px',
                              fontWeight: '700',
                              color: '#1f2937',
                              borderRight: '1px solid #e5e7eb',
                            }}
                          >
                            Status
                          </th>
                          <th
                            style={{
                              padding: '12px',
                              textAlign: 'center',
                              fontSize: '13px',
                              fontWeight: '700',
                              color: '#1f2937',
                            }}
                          >
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sampleSignSettings.map((sample, index) => (
                          <tr 
                            key={index} 
                            style={{ 
                              borderBottom: '1px solid #e5e7eb',
                              backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f3f4f6'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'
                            }}
                          >
                            <td
                              style={{
                                padding: '12px',
                                fontSize: '13px',
                                color: '#4b5563',
                                borderRight: '1px solid #e5e7eb',
                                fontWeight: '600',
                                textAlign: 'center',
                              }}
                            >
                              {index + 1}
                            </td>
                            <td
                              style={{
                                padding: '12px',
                                fontSize: '13px',
                                color: '#6b7280',
                                borderRight: '1px solid #e5e7eb',
                                fontWeight: '500',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {sample.sample_id}
                            </td>
                            <td
                              style={{
                                padding: '12px',
                                fontSize: '13px',
                                color: '#111827',
                                fontWeight: '600',
                                borderRight: '1px solid #e5e7eb',
                              }}
                            >
                              {sample.sample_name}
                            </td>
                            <td
                              style={{
                                padding: '12px',
                                fontSize: '13px',
                                color: '#4b5563',
                                borderRight: '1px solid #e5e7eb',
                              }}
                            >
                              {sample.document_caption || '-'}
                            </td>
                            <td
                              style={{
                                padding: '12px',
                                fontSize: '13px',
                                color: '#6b7280',
                                borderRight: '1px solid #e5e7eb',
                                fontStyle: sample.remark ? 'italic' : 'normal',
                              }}
                            >
                              {sample.remark || '-'}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                              <span
                                style={{
                                  padding: '6px 14px',
                                  borderRadius: '16px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  display: 'inline-block',
                                  backgroundColor:
                                    sample.sign_status === 'Active'
                                      ? '#d1fae5'
                                      : sample.sign_status === 'InActive'
                                      ? '#fee2e2'
                                      : '#fef3c7',
                                  color:
                                    sample.sign_status === 'Active'
                                      ? '#065f46'
                                      : sample.sign_status === 'InActive'
                                      ? '#991b1b'
                                      : '#92400e',
                                  border:
                                    sample.sign_status === 'Active'
                                      ? '1px solid #10b981'
                                      : sample.sign_status === 'InActive'
                                      ? '1px solid #ef4444'
                                      : '1px solid #f59e0b',
                                }}
                              >
                                {sample.sign_status || 'Not Found'}
                                {sample.is_newly_added && (
                                  <span style={{ marginLeft: '6px', fontSize: '10px', opacity: 0.8 }}>(New)</span>
                                )}
                              </span>
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <button
                                type="button"
                                onClick={() => toggleSampleStatus(sample)}
                                disabled={updatingSampleId === sample.sign_id}
                                style={{
                                  padding: '6px 14px',
                                  borderRadius: '16px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  border: 'none',
                                  cursor: updatingSampleId === sample.sign_id ? 'not-allowed' : 'pointer',
                                  backgroundColor:
                                    sample.sign_status === 'Active'
                                      ? '#ef4444'
                                      : COLORS.primary_color || '#3b82f6',
                                  color: '#ffffff',
                                  boxShadow:
                                    sample.sign_status === 'Active'
                                      ? '0 2px 6px rgba(239, 68, 68, 0.35)'
                                      : '0 2px 6px rgba(37, 99, 235, 0.35)',
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                {updatingSampleId === sample.sign_id
                                  ? 'Saving...'
                                  : sample.sign_status === 'Active'
                                  ? 'Deactivate'
                                  : 'Activate'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: '#6b7280' }}>
                    <span>Total Samples: <strong>{sampleSignSettings.length}</strong></span>
                    <span>
                      Active: <strong style={{ color: '#10b981' }}>{sampleSignSettings.filter(s => s.sign_status === 'Active').length}</strong> | 
                      InActive: <strong style={{ color: '#ef4444' }}>{sampleSignSettings.filter(s => s.sign_status === 'InActive').length}</strong> | 
                      Not Found: <strong style={{ color: '#f59e0b' }}>{sampleSignSettings.filter(s => !s.sign_status || s.sign_status === 'Not Found').length}</strong>
                    </span>
                  </div>
                </div>
              )}
              
              {selectedSignId && loadingSamples && (
                <div style={{ marginTop: '24px', padding: '40px', textAlign: 'center', borderTop: '2px solid #e5e7eb' }}>
                  <MyLoader />
                  <p style={{ marginTop: '16px', color: '#6b7280', fontSize: '14px' }}>Loading sample settings...</p>
                </div>
              )}
              
              {selectedSignId && !loadingSamples && sampleSignSettings.length === 0 && (
                <div style={{ marginTop: '24px', padding: '40px', textAlign: 'center', borderTop: '2px solid #e5e7eb', color: '#6b7280' }}>
                  <i className="fa fa-info-circle" style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.5 }}></i>
                  <p style={{ fontSize: '14px', fontWeight: '600' }}>No sample settings found for this sign</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Sign Modal */}
      <MdlManageDigitalSign
        show={showAddModal || showEditModal}
        mode={showAddModal ? 'add' : 'edit'}
        sign={selectedSign}
        actionClose={(didSave) => {
          setShowAddModal(false)
          setShowEditModal(false)
          setSelectedSign(null)
          if (didSave) {
            fetchSchoolDigitalSign()
          }
        }}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => { setShowDeleteModal(false); setSelectedSign(null) }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete the sign for <strong>{selectedSign?.authority_name || 'this authority'}</strong>?</p>
          <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowDeleteModal(false); setSelectedSign(null) }}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  )
}

export default SignSettings











































